/**
 * Phase 8 · Cluster C-48 — Agent plan persistence + state machine.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2 acceptance row 14
 *
 * Durable store for every plan the agent proposes before execution
 * (`ggj_b2b_agent_plans`, migration 112). The runner
 * (agentRunnerService) creates a row when the LLM returns ≥ 2 tool
 * calls or any write-capable call; the Plan card UI (C-48.4) calls
 * `approvePlan` / `rejectPlan` based on user action; the runner's
 * `executePlan` method then walks the steps and calls `recordStep`
 * after each invocation so every executed tool's audit row
 * (`ggj_b2b_agent_tool_invocations`) links back via `plan_id` /
 * `step_index`.
 *
 * State machine (CHECK constraint in migration 112):
 *     pending → approved | rejected | cancelled
 *     approved → executing → done | failed
 *
 * Persistence rules (spec §34.2.C "DON'T"s):
 *   - `steps` is a TYPED PlanStep[], NEVER the raw OpenAI/Ollama
 *     tool_calls JSON. Replay must work from the DB alone.
 *   - RLS enforces org-scoping via the parent thread — see migration
 *     112's `agent_plans_*_via_thread` policies. We never explicitly
 *     trust the caller's `orgId` to decide who can see what.
 */
import { supabase } from '../client';

/**
 * A single executable step in an agent plan. Must match the shape
 * persisted into `ggj_b2b_agent_plans.steps` (JSONB) and the runtime
 * envelope emitted by `agentRunnerService` on the `plan` event.
 */
export interface PlanStep {
  /** Registered tool name (e.g. "write.candidates.shortlist"). */
  toolName: string;
  /** JSON-serialisable args object passed to `toolRegistry.dispatch`. */
  argsJson: Record<string, unknown>;
  /** One-line natural-language reason — rendered on the Plan card. */
  rationale: string;
  /** Drives the Reversible/Permanent badge on the Plan card. */
  reversible: boolean;
}

export type AgentPlanApprovalState =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'executing'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface AgentPlanRow {
  id: string;
  threadId: string;
  turnId: string;
  steps: PlanStep[];
  approvalState: AgentPlanApprovalState;
  approvedBy: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  result: Record<string, unknown> | null;
  createdAt: string;
}

type DbRow = {
  id: string;
  thread_id: string;
  turn_id: string;
  steps: unknown;
  approval_state: string;
  approved_by: string | null;
  approved_at: string | null;
  executed_at: string | null;
  result: unknown;
  created_at: string;
};

function parseApprovalState(s: string): AgentPlanApprovalState {
  switch (s) {
    case 'pending':
    case 'approved':
    case 'rejected':
    case 'executing':
    case 'done':
    case 'failed':
    case 'cancelled':
      return s;
    default:
      return 'pending';
  }
}

function parseSteps(raw: unknown): PlanStep[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const rec = r as Record<string, unknown>;
      const toolName = typeof rec.toolName === 'string' ? rec.toolName : null;
      const rationale = typeof rec.rationale === 'string' ? rec.rationale : '';
      const reversible = typeof rec.reversible === 'boolean' ? rec.reversible : false;
      const argsJson =
        rec.argsJson && typeof rec.argsJson === 'object'
          ? (rec.argsJson as Record<string, unknown>)
          : {};
      if (!toolName) return null;
      return { toolName, argsJson, rationale, reversible } satisfies PlanStep;
    })
    .filter((x): x is PlanStep => x !== null);
}

function mapRow(row: DbRow): AgentPlanRow {
  return {
    id: row.id,
    threadId: row.thread_id,
    turnId: row.turn_id,
    steps: parseSteps(row.steps),
    approvalState: parseApprovalState(row.approval_state),
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    executedAt: row.executed_at,
    result: row.result && typeof row.result === 'object'
      ? (row.result as Record<string, unknown>)
      : null,
    createdAt: row.created_at,
  };
}

export const agentPlanService = {
  /**
   * Persist a new plan. Returns the full row so the runner can emit it
   * on the stream with a stable `planId`. `steps` is stored as-is in
   * the JSONB column — parsing happens on the read path.
   */
  async createPlan(
    threadId: string,
    turnId: string,
    steps: PlanStep[],
  ): Promise<AgentPlanRow | null> {
    if (!threadId || !turnId) return null;
    if (!Array.isArray(steps) || steps.length === 0) return null;

    const { data, error } = await supabase
      .from('ggj_b2b_agent_plans')
      .insert({
        thread_id: threadId,
        turn_id: turnId,
        steps,
        approval_state: 'pending',
      })
      .select('*')
      .single();

    if (error) {
      console.warn('[agentPlanService.createPlan] insert failed', error.message);
      return null;
    }
    return mapRow(data as DbRow);
  },

  /**
   * Load a plan by id. Returns null when the row is missing or RLS
   * denies access — callers must handle both the same way (don't
   * execute).
   */
  async getById(planId: string): Promise<AgentPlanRow | null> {
    if (!planId) return null;
    const { data, error } = await supabase
      .from('ggj_b2b_agent_plans')
      .select('*')
      .eq('id', planId)
      .maybeSingle();
    if (error) {
      console.warn('[agentPlanService.getById] failed', error.message);
      return null;
    }
    return data ? mapRow(data as DbRow) : null;
  },

  /**
   * Flip `pending → approved`. Stamps approver + timestamp. No-ops if
   * the plan is already past `pending` so double-clicks on the Plan
   * card don't regress state.
   */
  async approvePlan(planId: string, userId: string): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'approved',
      approved_by: userId || null,
      approved_at: new Date().toISOString(),
    }, ['pending']);
  },

  /** Flip `pending → rejected`. */
  async rejectPlan(planId: string, userId: string): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'rejected',
      approved_by: userId || null,
      approved_at: new Date().toISOString(),
    }, ['pending']);
  },

  /** Flip `pending | approved → cancelled` — user dismissed the card. */
  async cancelPlan(planId: string): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'cancelled',
    }, ['pending', 'approved']);
  },

  /**
   * Flip `approved → executing` right before the runner starts the
   * first step. Idempotent: if the plan is already executing (race),
   * just returns the current row.
   */
  async markExecuting(planId: string): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'executing',
      executed_at: new Date().toISOString(),
    }, ['approved']);
  },

  /**
   * Flip `executing → done` with the per-step outcome bag. `result`
   * shape is intentionally open — the runner stores
   * { steps: Array<{stepIndex, toolName, ok, summary}> } so the Plan
   * card UI can render post-hoc step pills without re-fetching the
   * audit rows.
   */
  async markDone(planId: string, result: Record<string, unknown>): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'done',
      result,
    }, ['executing']);
  },

  /**
   * Flip `executing → failed` when a step errored and the runner
   * decided not to continue (user chose "abort"). The `error` string
   * is wrapped in `result` so the same JSONB column surfaces both
   * success bag and failure reason.
   */
  async markFailed(planId: string, error: string): Promise<AgentPlanRow | null> {
    return transition(planId, {
      approval_state: 'failed',
      result: { error: error.slice(0, 2000) },
    }, ['executing']);
  },

  /**
   * Link a single tool invocation row to its parent plan. The UPDATE
   * stamps `plan_id` + `step_index` on an existing
   * `ggj_b2b_agent_tool_invocations` row — we can't insert it here
   * because `toolRegistry.dispatch` has already written the row in
   * its finally block.
   *
   * Called from the runner's `executePlan` after each step so the
   * acceptance query ("SELECT … WHERE plan_id=…") returns N rows
   * with matching `step_index`.
   */
  async recordStep(planId: string, stepIndex: number, invocationId: string): Promise<void> {
    if (!planId || !invocationId) return;
    const { error } = await supabase
      .from('ggj_b2b_agent_tool_invocations')
      .update({ plan_id: planId, step_index: stepIndex })
      .eq('id', invocationId);
    if (error) {
      console.warn('[agentPlanService.recordStep] failed', error.message);
    }
  },

  /**
   * Newest-first list of pending plans for a thread, used by the UI to
   * re-hydrate the Plan card after a refresh / device hand-off.
   */
  async listPendingByThread(threadId: string): Promise<AgentPlanRow[]> {
    if (!threadId) return [];
    const { data, error } = await supabase
      .from('ggj_b2b_agent_plans')
      .select('*')
      .eq('thread_id', threadId)
      .eq('approval_state', 'pending')
      .order('created_at', { ascending: false });
    if (error) {
      console.warn('[agentPlanService.listPendingByThread] failed', error.message);
      return [];
    }
    return ((data ?? []) as DbRow[]).map(mapRow);
  },
};

/**
 * Shared UPDATE helper — runs `UPDATE … WHERE id=$planId AND
 * approval_state IN (…expected)` so a concurrent transition can't
 * regress state. Returns the post-update row, or null if the guard
 * rejected the write (already transitioned / gone).
 */
async function transition(
  planId: string,
  patch: Record<string, unknown>,
  expected: AgentPlanApprovalState[],
): Promise<AgentPlanRow | null> {
  if (!planId) return null;
  const { data, error } = await supabase
    .from('ggj_b2b_agent_plans')
    .update(patch)
    .eq('id', planId)
    .in('approval_state', expected)
    .select('*')
    .maybeSingle();
  if (error) {
    console.warn('[agentPlanService.transition] failed', error.message);
    return null;
  }
  return data ? mapRow(data as DbRow) : null;
}
